// File: src/pages/RawProductList.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
} from "reactstrap";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import swal from "sweetalert";

import { FaPlus, FaFilter, FaPencilAlt } from "react-icons/fa";
import { ImDownload } from "react-icons/im";
import { Trash2 } from "react-feather";

// --- app-layer (as per your instructions) ---
import { CheckPermission } from "../import/CheckPermission";
import SuperAdminUI from "../import/SuperAdminUI";
import { exportDataToPDF } from "../import/Downloader";
import {
  _GetDataBydatabaseName,
  _Delete, // we'll use the generic delete
} from "../import/ApiEndPoint/ApiCalling";
import { deleteOneRawProduct } from "../import/ApiEndPoint/Api";

// (Optional) sample format link if you want to keep it in the menu
import UploadProductSample from "../import/UploadFormats/UploadProductSample.xlsx";

// ---- tiny CSS injected ONCE (page scoped) ----
const injectOnce = () => {
  if (document.getElementById("rawproducts-inline-css")) return;
  const style = document.createElement("style");
  style.id = "rawproducts-inline-css";
  style.innerHTML = `
    .rp-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .rp-table-wrap{
      width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff;
    }
    .rp-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:1000px; }
    .rp-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .rp-table tbody td{
      padding:8px 10px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .rp-table tbody tr:hover{ background:#f9fbff; }

    .action-chips{ display:flex; justify-content:center; align-items:center; gap:8px }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; }
    .chip-edit{ background:#d46f10; }
    .chip-delete{ background:#ec1809; }

    .units-badges{ display:flex; gap:6px; flex-wrap:wrap; justify-content:center; }
    .u-pill{ font-size:10px; padding:4px 6px; border:1px solid #e5e7eb; border-radius:999px; background:#f8fafc; }
  `;
  document.head.appendChild(style);
};

// ---- helpers ----
const toStr = (v) => (v === null || v === undefined ? "" : String(v));

export default function RawProductList() {
  useEffect(() => injectOnce(), []);
  const navigate = useNavigate();

  // ui
  const [loading, setLoading] = useState(true);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [modal, setModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // permissions
  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
    BulkUpload: false,
  });

  const [masterShow, setMasterShow] = useState(false);

  // data
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);

  // search
  const [search, setSearch] = useState("");

  // column chooser
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const addBufferRef = useRef([]);

  // ----- Define columns (no Actions here; appended later) -----
  const baseColumns = useMemo(
    () => [
      {
        key: "warehouseId",
        header: "WAREHOUSE ID",
        get: (r) => r?.warehouse?.id || r?.warehouse || "-",
      },
      {
        key: "productName",
        header: "PRODUCT NAME",
        get: (r) => r?.Product_Title,
        render: (r) => (
          <Link
            style={{ color: "black" }}
            to={`/apps/freshlist/Production/EditRawMateirals/${r?._id}`}
          >
            {r?.Product_Title || "-"}
          </Link>
        ),
      },
      {
        key: "minAlert",
        header: "MIN STOCKALERT",
        get: (r) => r?.MIN_stockalert,
      },
      {
        key: "openingStock",
        header: "OPENING STOCK",
        get: (r) => r?.Opening_Stock,
      },
      { key: "category", header: "CATEGORY", get: (r) => r?.category },
      {
        key: "subCategory",
        header: "SUB CATEGORY",
        get: (r) => r?.SubCategory,
      },
      { key: "step", header: "STEP", get: (r) => r?.SubStep },
      { key: "rawStep", header: "RAW STEP", get: (r) => r?.NextSubStep },
      { key: "stockUnit", header: "STOCK UNIT", get: (r) => r?.stockUnit },
      {
        key: "secondaryUnit",
        header: "SECONDARY UNIT",
        get: (r) => r?.secondaryUnit,
      },
      {
        key: "units",
        header: "UNITS",
        get: (r) =>
          Array.isArray(r?.Units)
            ? r.Units.map((u) => `${u.unit} (${u.qty})`).join(", ")
            : "",
        render: (r) => {
          const list = Array.isArray(r?.Units) ? r.Units : [];
          if (!list.length) return "-";
          return (
            <div className="units-badges">
              {list.map((u, i) => (
                <span key={i} className="u-pill">
                  {u?.unit} ({u?.qty})
                </span>
              ))}
            </div>
          );
        },
      },
      { key: "status", header: "STATUS", get: (r) => r?.status || "-" },
    ],
    []
  );

  const actionsColumn = useMemo(
    () => ({
      key: "__actions__",
      header: "ACTIONS",
      get: () => "",
      render: (r) => (
        <div className="action-chips">
          {permissions?.Edit && (
            <span
              className="chip chip-edit"
              title="Edit"
              onClick={() => navigate(`/app/raw-product/edit/${r?._id}`)}
            >
              <FaPencilAlt size={14} color="white" />
            </span>
          )}
          {permissions?.Delete && (
            <span
              className="chip chip-delete"
              title="Delete"
              onClick={() => handleDelete(r?._id)}
            >
              <Trash2 size={14} color="white" />
            </span>
          )}
        </div>
      ),
    }),
    [permissions, navigate]
  );

  // Column map
  const colMap = useMemo(() => {
    const map = new Map();
    baseColumns.forEach((c) => map.set(c.key, c));
    map.set(actionsColumn.key, actionsColumn);
    return map;
  }, [baseColumns, actionsColumn]);

  // Restore or default columns
  const [selectedKeys, setSelectedKeys] = useState([]);
  useEffect(() => {
    const raw = localStorage.getItem("RawProductListCols");
    let next = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        next = parsed.filter((k) => colMap.has(k));
      }
    } catch {}
    if (!next.length) next = baseColumns.map((c) => c.key);
    setSelectedKeys(next);
  }, [baseColumns, colMap]);

  // ----- Fetch -----
  const reload = async (overrideDb = null) => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    const role = user?.rolename?.roleName;
    if (role === "MASTER") setMasterShow(true);

    // permissions
    const inside = CheckPermission("Raw Products");
    if (inside && typeof inside === "object") {
      setPermissions((p) => ({ ...p, ...inside }));
    }

    const db = overrideDb || user?.database;
    if (!db) throw new Error("Missing database in userData");
    const res = await _GetDataBydatabaseName(db);
    const list = Array.isArray(res?.Product) ? res.Product : [];
    setRows(list);
    setAllRows(list);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        await reload();
      } catch (e) {
        console.error("[RawProductList] fetch error:", e);
        setErrorMsg(
          e?.response?.data?.message ||
            e?.message ||
            "Unable to load raw products. Check console for details."
        );
        setRows([]);
        setAllRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ----- Visible columns & search -----
  const visibleCols = useMemo(() => {
    const cols = selectedKeys.map((k) => colMap.get(k)).filter(Boolean);
    return [...cols, actionsColumn]; // keep actions at end
  }, [selectedKeys, colMap, actionsColumn]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    const searchCols = visibleCols.filter((c) => c.key !== "__actions__");
    return rows.filter((r) =>
      searchCols.some((c) => toStr(c.get(r)).toLowerCase().includes(term))
    );
  }, [rows, search, visibleCols]);

  // ----- Actions -----
  const handleDelete = (id) => {
    swal("Warning", "Sure you want to delete it?", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then((value) => {
      if (value === "delete") {
        _Delete(deleteOneRawProduct, id)
          .then(() => {
            setRows((prev) => prev.filter((x) => x._id !== id));
            setAllRows((prev) => prev.filter((x) => x._id !== id));
          })
          .catch(() => {});
      }
    });
  };

  // ----- Export -----
  const handleExportPDF = async () => {
    const cols = visibleCols.filter((c) => c.key !== "__actions__");
    const header = cols.map((c) => c.header).join(",");
    const lines = filteredRows.map((r) =>
      cols
        .map((c) => {
          const v = toStr(c.get(r)).replace(/"/g, '""');
          return `"${v}"`;
        })
        .join(",")
    );
    const csv = [header, ...lines].join("\r\n");
    await exportDataToPDF(csv, "RawProductList");
  };

  const handleExportXLSX = (fileName) => {
    const cols = visibleCols.filter((c) => c.key !== "__actions__");
    const data = filteredRows.map((r) => {
      const obj = {};
      cols.forEach((c) => (obj[c.header] = toStr(c.get(r))));
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${fileName}.xlsx`
    );
  };

  // ----- Column Chooser Modal -----
  const openModal = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModal(true);
  };
  const closeModal = () => setModal(false);

  const handleCheckbox = (checked, colKey) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(colKey)) buf.push(colKey);
    } else {
      const i = buf.indexOf(colKey);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };

  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current])
    );
    setSelectedKeys(next);
  };

  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };

  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };

  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };

  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem("RawProductListCols", JSON.stringify(selectedKeys));
    closeModal();
  };

  // ----- SuperAdmin switch (Master) -----
  const handleParentSubmit = async () => {
    const raw = JSON.parse(
      localStorage.getItem("SuperadminIdByMaster") || "null"
    );
    if (!raw) return;
    const [, db] = String(raw).split(" ");
    setLoading(true);
    setErrorMsg("");
    try {
      await reload(db);
    } catch (e) {
      setErrorMsg("Failed to load for selected SuperAdmin.");
    } finally {
      setLoading(false);
    }
  };
  const handleDropdownChange = (val) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(val));
  };

  // ----- Loading -----
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows?.length;

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* === Toolbar === */}
      <Row className="rp-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Raw Product List{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              {masterShow && (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <SuperAdminUI
                    onDropdownChange={handleDropdownChange}
                    onSubmit={handleParentSubmit}
                  />
                </div>
              )}

              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search raw products..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              {permissions?.Create && (
                <Button
                  style={{
                    backgroundColor: "rgb(8, 91, 245)",
                    color: "white",
                    fontWeight: 600,
                    height: 32,
                    fontSize: 14,
                    textTransform: "uppercase",
                  }}
                  onClick={() => navigate("/app/raw-product/create")}
                >
                  <FaPlus size={12} /> Product
                </Button>
              )}

              <Button
                outline
                color="primary"
                onClick={openModal}
                title="Filter Columns"
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaFilter size={14} />
              </Button>

              {permissions?.Download && (
                <div
                  onMouseEnter={() => setDownloadOpen(true)}
                  onMouseLeave={() => setDownloadOpen(false)}
                  style={{ position: "relative" }}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                  {downloadOpen && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 32,
                        zIndex: 5,
                        border: "1px solid rgb(8, 91, 245)",
                        backgroundColor: "white",
                        borderRadius: 10,
                        overflow: "hidden",
                        minWidth: 150,
                        boxShadow: "0 12px 24px rgba(0,0,0,.12)",
                      }}
                    >
                      <div onClick={handleExportPDF} style={dlItemStyle}>
                        .PDF
                      </div>
                      <div
                        onClick={() => handleExportXLSX("RawProductList")}
                        style={dlItemStyle}
                      >
                        .XLSX
                      </div>
                      {permissions?.BulkUpload && (
                        <a
                          href={UploadProductSample}
                          download
                          style={{
                            ...dlItemStyle,
                            display: "block",
                            color: "inherit",
                            textDecoration: "none",
                          }}
                        >
                          . FORMAT
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* === Table === */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No raw products found.
              </div>
            ) : (
              <div className="rp-table-wrap">
                <table className="rp-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r) => (
                      <tr key={r?._id || r?.id}>
                        {visibleCols.map((c) => (
                          <td key={c.key}>
                            {typeof c.render === "function"
                              ? c.render(r)
                              : toStr(c.get(r))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* === Column Chooser Modal === */}
      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div style={listBoxStyle}>
                {baseColumns.map((col) => (
                  <div key={col.key} style={pickItemStyle}>
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div style={{ ...listBoxStyle, flex: 1 }}>
                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        style={{
                          ...pickItemStyle,
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}

/* little styles */
const dlItemStyle = {
  padding: "10px 12px",
  cursor: "pointer",
  fontWeight: 600,
  letterSpacing: ".02em",
};
const listBoxStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  maxHeight: 280,
  overflow: "auto",
  background: "#fff",
  padding: 8,
};
const pickItemStyle = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  marginBottom: 6,
  background: "#fff",
};
